// Figures for the "Review & publish" step of the add-exam editor, computed
// only from the questions that were just saved (nothing is invented).

export interface ReviewQuestion {
  stem: string
  /** "A".."E" when the key is a letter, otherwise ''. */
  answerLetter: string
  /** Plain text of the correct choice (or the raw key when it is not a letter). */
  answerText: string
  chapter: string | null
  explained: boolean
}

export const stripHtml = (html: unknown): string =>
  String(html ?? '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()

const CHOICE_KEYS: Record<string, string> = { A: 'choice_a', B: 'choice_b', C: 'choice_c', D: 'choice_d', E: 'choice_e' }

/** Saved questions (the payload the pipeline sent) → rows for the review. `chapters[i]` is question i's chapter name. */
export function toReviewQuestions(saved: any[], chapters: Array<string | null | undefined> = []): ReviewQuestion[] {
  return saved.map((q, i) => {
    const raw = String(q?.ans ?? q?.answer ?? '').trim()
    const letter = raw.toUpperCase()
    const isLetter = letter in CHOICE_KEYS
    const explanation = String(q?.explanation ?? '')
    return {
      stem: stripHtml(q?.ques ?? q?.question) || 'Untitled question',
      answerLetter: isLetter ? letter : '',
      answerText: isLetter ? stripHtml(q?.[CHOICE_KEYS[letter]]) : stripHtml(raw),
      chapter: (chapters[i] ?? '').toString().trim() || null,
      explained: stripHtml(explanation) !== '' || /<img/i.test(explanation),
    }
  })
}

export interface CoverageRow { name: string; count: number; pct: number }

/** Questions per chapter, widest bar = the most-covered chapter. Questions without a chapter are left out. */
export function chapterCoverage(qs: ReviewQuestion[]): CoverageRow[] {
  const counts = new Map<string, number>()
  qs.forEach((q) => { if (q.chapter) counts.set(q.chapter, (counts.get(q.chapter) ?? 0) + 1) })
  const max = Math.max(1, ...Array.from(counts.values()))
  return Array.from(counts, ([name, count]) => ({ name, count, pct: Math.round((count / max) * 100) }))
}

export interface ReviewStats {
  total: number
  keysSet: number
  withoutExplanation: number
  chaptersCovered: number
  /** Minutes per question, rounded; null without a time or questions. */
  minutesPerQuestion: number | null
  /** Index of the first question with no explanation, or -1. */
  firstUnexplained: number
}

export function reviewStats(qs: ReviewQuestion[], timeMinutes: number | null): ReviewStats {
  const total = qs.length
  return {
    total,
    keysSet: qs.filter((q) => q.answerLetter !== '' || q.answerText !== '').length,
    withoutExplanation: qs.filter((q) => !q.explained).length,
    chaptersCovered: chapterCoverage(qs).length,
    minutesPerQuestion: timeMinutes && total > 0 ? Math.round(timeMinutes / total) : null,
    firstUnexplained: qs.findIndex((q) => !q.explained),
  }
}

/** Who can take the exam: the head-count and the caption under it. null count = not known. */
export function audienceSummary(opts: {
  mode: 'grade' | 'sections' | 'students'
  gradeStudentCount: number | null
  sections: Array<{ id: number; name: string; student_count: number }>
  sectionIds: number[]
  studentIds: number[]
}): { count: number | null; caption: string } {
  if (opts.mode === 'students') return { count: opts.studentIds.length, caption: 'individually assigned' }
  if (opts.mode === 'sections') {
    const picked = opts.sections.filter((s) => opts.sectionIds.includes(s.id))
    return {
      count: picked.reduce((n, s) => n + s.student_count, 0),
      caption: `students · ${picked.length ? `Sections ${picked.map((s) => s.name).join(', ')}` : 'no sections'}`,
    }
  }
  return { count: opts.gradeStudentCount, caption: 'students · whole grade' }
}

/** The remote helpers swallow errors and return them; a response is a failure when it is one of those. */
export const isFailedResponse = (res: any): boolean =>
  res == null || res instanceof Error || res?.isAxiosError === true || (typeof res === 'object' && 'response' in res && 'message' in res);

/**
 * SchoolAddExamQuestions answers 201 even when some questions failed: `{ created, updated?, errors[] }`.
 * Returns the problems when fewer questions were stored than sent, or null when all were saved.
 */
export function partialSave(res: any, sent: number): { saved: number; errors: string[] } | null {
  if (!res || typeof res !== 'object') return null;
  const errors: string[] = Array.isArray(res.errors) ? res.errors.map((e: unknown) => String(e)) : [];
  const stored = (Number(res.created) || 0) + (Number(res.updated) || 0);
  const known = typeof res.created === 'number';
  if (errors.length === 0 && (!known || stored >= sent)) return null;
  return { saved: known ? Math.min(stored, sent) : Math.max(0, sent - errors.length), errors };
}

/** "saved just now" / "saved 2 min ago" / "saved 3 h ago" / "saved 12 Oct". */
export function savedAgo(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return null
  const mins = Math.floor((now.getTime() - t.getTime()) / 60000)
  if (mins < 1) return 'saved just now'
  if (mins < 60) return `saved ${mins} min ago`
  if (mins < 60 * 24) return `saved ${Math.floor(mins / 60)} h ago`
  return `saved ${t.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`
}

/**
 * Posts the questions to each exam in turn (Mixed exam creates several). Exams that already took them
 * (`savedIds`) are skipped, so a retry never duplicates; partial-save warnings accumulate in `warnings`
 * across attempts so none is lost when another exam fails in the same attempt.
 */
export async function saveToExams(opts: {
  examIds: string[]
  sent: number
  post: (examId: string) => Promise<any>
  savedIds: Set<string>
  warnings: string[]
}): Promise<{ failed: string[]; results: any[] }> {
  const failed: string[] = [];
  const results: any[] = [];
  for (const exId of opts.examIds) {
    if (opts.savedIds.has(exId)) continue;
    try {
      const resp = await opts.post(exId);
      if (isFailedResponse(resp)) { failed.push(exId); results.push({ exam: exId, error: resp }); continue; }
      opts.savedIds.add(exId);
      const part = partialSave(resp, opts.sent);
      if (part) {
        opts.warnings.push(`${opts.sent - part.saved} of ${opts.sent} questions could not be saved to exam ${exId}${part.errors.length ? ` (${part.errors.slice(0, 3).join('; ')})` : ''}`);
      }
      results.push({ exam: exId, resp });
    } catch (err) { failed.push(exId); results.push({ exam: exId, error: err }); }
  }
  return { failed, results };
}
